import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import { calculateRegistrationPrice } from 'src/helpers/registrationPrice';
import {
  WizardCheckinDraft,
  WizardFindOption,
  WizardRegisterDraft,
  buildSummaryRows,
  buildWizardPriceBreakdown,
  canRegisterParticipant,
  getDefaultSelectedParticipantIds,
  getParticipantRegistrationStatus,
  getSummaryPriceItems,
  partitionByRegistrationStatus,
} from 'src/helpers/userCheckinWizard';
import { useQrCheckinScan } from 'src/hooks/useQrCheckinScan';
import { ICheckin } from 'src/interfaces/Checkin';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { LanguageKeys } from 'src/common/constants/languages';
import { IUser } from 'src/interfaces/User';
import useCheckinsStore from 'src/stores/checkinsStore';
import useParticipantsStore from 'src/stores/participantsStore';
import useRegistrationsStore from 'src/stores/registrationsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import useUsersStore from 'src/stores/usersStore';

const WIZARD_STEPS = ['findUser', 'selectParticipants', 'register', 'checkin', 'summary'] as const;

const STEP_INDEX = {
  findUser: 0,
  selectParticipants: 1,
  register: 2,
  checkin: 3,
  summary: 4,
} as const;

export function useUserCheckinWizard({
  open,
  editionId,
  initialUserId,
  onComplete,
  onClose,
}: {
  open: boolean;
  editionId: string;
  initialUserId?: string;
  onComplete?: () => void;
  onClose: () => void;
}) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { users, fetchUsers, isLoading: isLoadingUsers } = useUsersStore();
  const { listParticipantsForOwner, listUnownedParticipantsForEdition } = useParticipantsStore();
  const { createRegistrationAsAdmin, isLoading: isLoadingRegistrations } = useRegistrationsStore();
  const { saveCheckin, isLoadingCheckins } = useCheckinsStore();
  const { activeEditionId, competitionsById, distancesById, editionsById } = useSystemOptionsStore();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [selectedFindOption, setSelectedFindOption] = useState<WizardFindOption | null>(null);
  const [unownedParticipants, setUnownedParticipants] = useState<IEnrichedParticipant[]>([]);
  const [userParticipants, setUserParticipants] = useState<IEnrichedParticipant[]>([]);
  const [isLoadingUserParticipants, setIsLoadingUserParticipants] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [registerDrafts, setRegisterDrafts] = useState<Record<string, WizardRegisterDraft>>({});
  const [checkinDrafts, setCheckinDrafts] = useState<Record<string, WizardCheckinDraft>>({});
  const [checkinsByParticipantId, setCheckinsByParticipantId] = useState<Record<string, ICheckin>>({});
  const [isRegistering, setIsRegistering] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);

  const activeEdition = activeEditionId && editionsById ? editionsById[activeEditionId] : undefined;

  const resetWizard = useCallback(() => {
    setActiveStep(0);
    setSelectedUser(null);
    setSelectedFindOption(null);
    setUnownedParticipants([]);
    setUserParticipants([]);
    setSelectedParticipantIds([]);
    setRegisterDrafts({});
    setCheckinDrafts({});
    setCheckinsByParticipantId({});
    setIsRegistering(false);
    setIsCheckingIn(false);
    setQrScannerOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetWizard();
      return;
    }
    void fetchUsers();
  }, [open, fetchUsers, resetWizard]);

  useEffect(() => {
    if (!open || !editionId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await listUnownedParticipantsForEdition(editionId);
        if (!cancelled) {
          setUnownedParticipants(data);
        }
      } catch (error) {
        if (!cancelled) {
          setUnownedParticipants([]);
          enqueueSnackbar(
            getAxiosErrorMessage(error, t('userCheckinWizard.errors.loadParticipants')),
            { variant: 'error' },
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, editionId, listUnownedParticipantsForEdition, enqueueSnackbar, t]);

  useEffect(() => {
    if (!open || !initialUserId || !users.length) {
      return;
    }
    const user = users.find(u => u.id === initialUserId);
    if (user) {
      setSelectedUser(user);
      setSelectedFindOption({ kind: 'user', id: `user:${user.id}`, user });
      setActiveStep(1);
    }
  }, [open, initialUserId, users]);

  const loadUserParticipants = useCallback(
    async (userId: string) => {
      if (!editionId) {
        return;
      }
      setIsLoadingUserParticipants(true);
      try {
        const data = await listParticipantsForOwner(editionId, userId);
        setUserParticipants(data);
        setSelectedParticipantIds(getDefaultSelectedParticipantIds(data));
      } catch (error) {
        enqueueSnackbar(getAxiosErrorMessage(error, t('userCheckinWizard.errors.loadParticipants')), {
          variant: 'error',
        });
        setUserParticipants([]);
        setSelectedParticipantIds([]);
      } finally {
        setIsLoadingUserParticipants(false);
      }
    },
    [editionId, listParticipantsForOwner, enqueueSnackbar, t],
  );

  const refreshSeededParticipants = useCallback(async () => {
    if (!editionId || selectedParticipantIds.length === 0) {
      return userParticipants;
    }
    const all = await listUnownedParticipantsForEdition(editionId);
    setUnownedParticipants(all);
    const byId = new Map(all.map(p => [p.id, p]));
    const refreshed = selectedParticipantIds
      .map(id => byId.get(id) ?? userParticipants.find(p => p.id === id))
      .filter((p): p is IEnrichedParticipant => p != null);
    setUserParticipants(refreshed);
    return refreshed;
  }, [
    editionId,
    selectedParticipantIds,
    userParticipants,
    listUnownedParticipantsForEdition,
  ]);

  useEffect(() => {
    if (!open || !selectedUser?.id || activeStep !== 1) {
      return;
    }
    void loadUserParticipants(selectedUser.id);
  }, [open, selectedUser?.id, activeStep, loadUserParticipants]);

  const handleSelectFindOption = useCallback((option: WizardFindOption | null) => {
    setSelectedFindOption(option);
    if (!option) {
      setSelectedUser(null);
      setUserParticipants([]);
      setSelectedParticipantIds([]);
      return;
    }
    if (option.kind === 'user') {
      setSelectedUser(option.user);
      setUserParticipants([]);
      setSelectedParticipantIds([]);
      return;
    }
    setSelectedUser(null);
    setUserParticipants([option.participant]);
    setSelectedParticipantIds(getDefaultSelectedParticipantIds([option.participant]));
  }, []);

  const selectedParticipants = useMemo(
    () => userParticipants.filter(p => selectedParticipantIds.includes(p.id)),
    [userParticipants, selectedParticipantIds],
  );

  const { unregistered, registered, checkedIn } = useMemo(
    () => partitionByRegistrationStatus(selectedParticipants),
    [selectedParticipants],
  );

  const unregisteredSelected = useMemo(
    () => unregistered.filter(p => selectedParticipantIds.includes(p.id)),
    [unregistered, selectedParticipantIds],
  );

  const needsRegistration = unregisteredSelected.length > 0;

  const registeredToCheckIn = useMemo(
    () =>
      selectedParticipants.filter(
        p => getParticipantRegistrationStatus(p) === 'registered',
      ),
    [selectedParticipants],
  );

  const canCheckIn = registeredToCheckIn.length > 0;

  const hasCheckedInSelected = useMemo(
    () =>
      selectedParticipants.some(
        p =>
          getParticipantRegistrationStatus(p) === 'checkedIn' || !!checkinsByParticipantId[p.id],
      ),
    [selectedParticipants, checkinsByParticipantId],
  );

  /** Next is blocked on the check-in step until at least one selected participant is checked in. */
  const canAdvanceFromCheckin = hasCheckedInSelected || !canCheckIn;

  const initializeRegisterDrafts = useCallback(() => {
    if (!activeEdition) {
      return;
    }
    const drafts: Record<string, WizardRegisterDraft> = {};
    unregisteredSelected.forEach(p => {
      const firstComp = activeEdition.competitions?.[0];
      const competitionId = firstComp?.competitionId ?? '';
      const distanceId = firstComp?.distances?.[0] ?? '';
      const price =
        competitionId && distanceId
          ? calculateRegistrationPrice(
              activeEdition,
              {
                dateOfBirth: p.dateOfBirth,
                age: p.age,
                discounted: p.discounted,
              },
              { isAdminContext: true },
            )
          : 0;
      drafts[p.id] = {
        competitionId,
        distanceId,
        price,
      };
    });
    setRegisterDrafts(drafts);
  }, [activeEdition, unregisteredSelected]);

  const initializeCheckinDrafts = useCallback((participants: IEnrichedParticipant[]) => {
    const drafts: Record<string, WizardCheckinDraft> = {};
    participants.forEach(p => {
      if (getParticipantRegistrationStatus(p) !== 'registered' || !p.registration) {
        return;
      }
      drafts[p.id] = {
        competitionId: p.registration.competitionId,
        distanceId: p.registration.distanceId,
        price: p.registration.price ?? 0,
      };
    });
    setCheckinDrafts(drafts);
  }, []);

  const handleNext = useCallback(() => {
    if (activeStep === 0 && !selectedFindOption) {
      return;
    }
    if (activeStep === STEP_INDEX.selectParticipants) {
      if (selectedParticipantIds.length === 0) {
        enqueueSnackbar(t('userCheckinWizard.errors.noSelection'), { variant: 'warning' });
        return;
      }
      initializeRegisterDrafts();
      if (!needsRegistration) {
        initializeCheckinDrafts(selectedParticipants);
        setActiveStep(STEP_INDEX.checkin);
        return;
      }
    }
    if (activeStep === STEP_INDEX.register) {
      if (selectedUser?.id) {
        void loadUserParticipants(selectedUser.id);
      } else {
        void refreshSeededParticipants();
      }
    }
    setActiveStep(prev => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  }, [
    activeStep,
    selectedFindOption,
    selectedUser,
    selectedParticipantIds,
    selectedParticipants,
    needsRegistration,
    initializeRegisterDrafts,
    initializeCheckinDrafts,
    loadUserParticipants,
    refreshSeededParticipants,
    enqueueSnackbar,
    t,
  ]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => {
      if (prev === STEP_INDEX.checkin && !needsRegistration) {
        return STEP_INDEX.selectParticipants;
      }
      return Math.max(prev - 1, 0);
    });
  }, [needsRegistration]);

  const updateRegisterDraft = useCallback(
    (participantId: string, patch: Partial<WizardRegisterDraft>) => {
      setRegisterDrafts(prev => {
        const existing = prev[participantId];
        if (!existing) return prev;
        return {
          ...prev,
          [participantId]: { ...existing, ...patch },
        };
      });
    },
    [],
  );

  const updateCheckinDraft = useCallback(
    (participantId: string, patch: Partial<WizardCheckinDraft>) => {
      setCheckinDrafts(prev => {
        const existing = prev[participantId];
        if (!existing) return prev;
        return {
          ...prev,
          [participantId]: { ...existing, ...patch },
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (activeStep !== STEP_INDEX.checkin) {
      return;
    }
    if (registeredToCheckIn.length === 0) {
      return;
    }
    setCheckinDrafts(prev => {
      const next = { ...prev };
      let changed = false;
      registeredToCheckIn.forEach(p => {
        if (next[p.id] || !p.registration) {
          return;
        }
        next[p.id] = {
          competitionId: p.registration.competitionId,
          distanceId: p.registration.distanceId,
          price: p.registration.price ?? 0,
        };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [activeStep, registeredToCheckIn]);

  const handleRegisterAll = useCallback(async () => {
    if (!activeEditionId) {
      return;
    }
    const toRegister = unregisteredSelected.filter(
      p => canRegisterParticipant(p) && registerDrafts[p.id]?.competitionId && registerDrafts[p.id]?.distanceId,
    );
    if (!toRegister.length) {
      enqueueSnackbar(t('userCheckinWizard.errors.nothingToRegister'), { variant: 'warning' });
      return;
    }

    setIsRegistering(true);
    let successCount = 0;
    let failCount = 0;

    for (const p of toRegister) {
      const draft = registerDrafts[p.id]!;
      try {
        await createRegistrationAsAdmin({
          participantId: p.id,
          editionId: activeEditionId,
          competitionId: draft.competitionId,
          distanceId: draft.distanceId,
          price: draft.price,
        });
        successCount += 1;
      } catch (error) {
        failCount += 1;
        enqueueSnackbar(
          `${p.firstName} ${p.lastName}: ${getAxiosErrorMessage(error, t('userCheckinWizard.errors.registerFailed'))}`,
          { variant: 'error' },
        );
      }
    }

    setIsRegistering(false);

    if (successCount > 0) {
      enqueueSnackbar(
        t('userCheckinWizard.registerSuccess', { count: successCount }),
        { variant: 'success' },
      );
      if (selectedUser?.id) {
        await loadUserParticipants(selectedUser.id);
      } else {
        await refreshSeededParticipants();
      }
    }
    if (failCount > 0 && successCount === 0) {
      enqueueSnackbar(t('userCheckinWizard.errors.registerFailed'), { variant: 'error' });
    }
  }, [
    activeEditionId,
    unregisteredSelected,
    registerDrafts,
    createRegistrationAsAdmin,
    enqueueSnackbar,
    t,
    selectedUser?.id,
    loadUserParticipants,
    refreshSeededParticipants,
  ]);

  const handleCheckInAll = useCallback(async () => {
    if (!activeEditionId) {
      return;
    }

    const refreshed = selectedUser?.id
      ? await listParticipantsForOwner(editionId, selectedUser.id)
      : await refreshSeededParticipants();
    setUserParticipants(refreshed);

    const toCheckIn = refreshed.filter(p => {
      if (!selectedParticipantIds.includes(p.id)) {
        return false;
      }
      return getParticipantRegistrationStatus(p) === 'registered';
    });

    if (!toCheckIn.length) {
      enqueueSnackbar(t('userCheckinWizard.errors.nothingToCheckIn'), { variant: 'warning' });
      return;
    }

    setIsCheckingIn(true);
    const newCheckins: Record<string, ICheckin> = { ...checkinsByParticipantId };
    let successCount = 0;

    for (const p of toCheckIn) {
      const reg = p.registration;
      if (!reg) {
        continue;
      }
      const draft = checkinDrafts[p.id];
      const competition = draft?.competitionId || reg.competitionId;
      const distance = draft?.distanceId || reg.distanceId;
      const price =
        typeof draft?.price === 'number' && !Number.isNaN(draft.price) ? draft.price : reg.price;
      const payload: {
        participant: string;
        edition: string;
        competition: string;
        distance: string;
        priceOverride?: number;
      } = {
        participant: p.id,
        edition: activeEditionId,
        competition,
        distance,
      };
      if (typeof price === 'number' && !Number.isNaN(price) && price !== reg.price) {
        payload.priceOverride = price;
      }
      try {
        const created = await saveCheckin(payload);
        if (created) {
          newCheckins[p.id] = created;
          successCount += 1;
        }
      } catch (error) {
        enqueueSnackbar(
          `${p.firstName} ${p.lastName}: ${getAxiosErrorMessage(error, t('userCheckinWizard.errors.checkInFailed'))}`,
          { variant: 'error' },
        );
      }
    }

    setCheckinsByParticipantId(newCheckins);
    setIsCheckingIn(false);

    if (successCount > 0) {
      enqueueSnackbar(t('userCheckinWizard.checkInSuccess', { count: successCount }), {
        variant: 'success',
      });
      if (selectedUser?.id) {
        const updated = await listParticipantsForOwner(editionId, selectedUser.id);
        setUserParticipants(updated);
        updated.forEach(p => {
          if (p.checkin) {
            newCheckins[p.id] = p.checkin as ICheckin;
          }
        });
        setCheckinsByParticipantId({ ...newCheckins });
      } else {
        const updated = await refreshSeededParticipants();
        updated.forEach(p => {
          if (p.checkin) {
            newCheckins[p.id] = p.checkin as ICheckin;
          }
        });
        setCheckinsByParticipantId({ ...newCheckins });
      }
    }
  }, [
    activeEditionId,
    selectedUser?.id,
    editionId,
    listParticipantsForOwner,
    refreshSeededParticipants,
    selectedParticipantIds,
    checkinDrafts,
    saveCheckin,
    checkinsByParticipantId,
    enqueueSnackbar,
    t,
  ]);

  const { handleScan: handleQrScan } = useQrCheckinScan({
    editionId: activeEditionId || editionId,
    onResolved: result => {
      if (!result.ownerUserId) {
        enqueueSnackbar(t('checkins.qrNoAccountUser'), { variant: 'warning' });
        return;
      }
      const user = users.find(u => u.id === result.ownerUserId);
      if (!user) {
        enqueueSnackbar(t('checkins.qrNoAccountUser'), { variant: 'warning' });
        return;
      }
      setSelectedUser(user);
      setSelectedFindOption({ kind: 'user', id: `user:${user.id}`, user });
      setActiveStep(STEP_INDEX.selectParticipants);
    },
    enqueueSnackbar,
    t,
    onSuccess: () => setQrScannerOpen(false),
  });

  const handleDone = useCallback(() => {
    onComplete?.();
    onClose();
  }, [onComplete, onClose]);

  const summaryParticipants = useMemo(() => {
    return userParticipants.filter(p => {
      if (!selectedParticipantIds.includes(p.id)) {
        return false;
      }
      return (
        getParticipantRegistrationStatus(p) === 'checkedIn' ||
        !!checkinsByParticipantId[p.id]
      );
    });
  }, [userParticipants, selectedParticipantIds, checkinsByParticipantId]);

  const summaryParticipantsForPrice = useMemo(() => {
    return userParticipants.filter(
      p => selectedParticipantIds.includes(p.id) && !!p.registration,
    );
  }, [userParticipants, selectedParticipantIds]);

  const summaryRows = useMemo(() => {
    if (!competitionsById || !distancesById) {
      return [];
    }
    const currentLang = resolvedLanguage as LanguageKeys;
    return buildSummaryRows(
      summaryParticipants.map(p => ({
        ...p,
        checkin: (p.checkin ?? checkinsByParticipantId[p.id]) as IEnrichedParticipant['checkin'],
      })),
      checkinsByParticipantId,
      competitionsById,
      distancesById,
      currentLang,
    );
  }, [summaryParticipants, checkinsByParticipantId, competitionsById, distancesById, resolvedLanguage]);

  const priceBreakdown = useMemo(
    () =>
      buildWizardPriceBreakdown(
        getSummaryPriceItems(summaryParticipantsForPrice, activeEdition),
      ),
    [summaryParticipantsForPrice, activeEdition],
  );

  const isBusy =
    isLoadingUsers ||
    isLoadingUserParticipants ||
    isLoadingRegistrations ||
    isLoadingCheckins ||
    isRegistering ||
    isCheckingIn;

  return {
    activeStep,
    stepIds: WIZARD_STEPS,
    selectedUser,
    selectedFindOption,
    handleSelectFindOption,
    users,
    unownedParticipants,
    isLoadingUsers,
    userParticipants,
    isLoadingUserParticipants,
    selectedParticipantIds,
    setSelectedParticipantIds,
    registerDrafts,
    updateRegisterDraft,
    checkinDrafts,
    updateCheckinDraft,
    selectedParticipants,
    unregisteredSelected,
    registered,
    checkedIn,
    needsRegistration,
    canCheckIn,
    hasCheckedInSelected,
    canAdvanceFromCheckin,
    registeredToCheckIn,
    activeEdition,
    competitionsById,
    distancesById,
    isBusy,
    isRegistering,
    isCheckingIn,
    handleNext,
    handleBack,
    handleRegisterAll,
    handleCheckInAll,
    handleDone,
    summaryRows,
    priceBreakdown,
    getParticipantRegistrationStatus,
    canRegisterParticipant,
    qrScannerOpen,
    openQrScanner: () => setQrScannerOpen(true),
    closeQrScanner: () => setQrScannerOpen(false),
    handleQrScan,
  };
}
